import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Send, Loader2, Sparkles, MessageCircle, Phone, User as UserIcon, MapPin, CheckCircle2, Copy, Share2, AlertTriangle, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const SAMPLE_QUERIES = [
  "I want a plumber near me — urgent tap leak",
  "Looking for a cardiologist in Bandra, budget under ₹2000",
  "Need a 2BHK for rent in Koregaon Park, under ₹35000/month",
  "Wedding photographer in Ahmedabad for 2 days, October",
];

const URGENCY_LABEL = {
  immediate:   { label: "URGENT · now",     color: "bg-red-100 text-red-700 border-red-200" },
  today:       { label: "Today",             color: "bg-orange-100 text-orange-700 border-orange-200" },
  this_week:   { label: "This week",         color: "bg-amber-100 text-amber-700 border-amber-200" },
  flexible:    { label: "Flexible timing",   color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

export default function LeadsNew() {
  const { user } = useAuth();
  const { loc: ctxLoc } = useUserLocation();
  const urlCity = new URLSearchParams(window.location.search).get("city") || "";
  // Never send the built-in default (Mumbai) as if it were the user's GPS; a city typed on /need wins.
  const loc = urlCity ? { city: urlCity } : ctxLoc && ctxLoc.source !== "default" ? ctxLoc : null;
  const [query, setQuery] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [shareContact, setShareContact] = useState(false);
  const [contact, setContact] = useState({ name: "", mobile: "" });   // for guest users
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    const q = query.trim();
    if (!q) return toast.error("Type what you need first.");
    if (!user && (!contact.mobile || contact.mobile.length < 10)) {
      return toast.error("Share your mobile so providers can reach you.");
    }
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/leads/generate", {
        query: q,
        lat: loc?.lat, lng: loc?.lng, city: loc?.city,
        contact_mobile: user ? undefined : contact.mobile,
        contact_name:   user ? undefined : contact.name,
        share_contact:  user ? shareContact : undefined,
      });
      setResult(r.data);
      if (r.data.broadcast_count > 0) {
        toast.success(`Broadcast to ${r.data.broadcast_count} provider${r.data.broadcast_count === 1 ? "" : "s"} · you'll get replies in your Inbox`);
      } else {
        toast.message("No registered providers matched — share the invite link below with anyone you know.");
      }
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Failed to generate lead — try again");
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch { /* clipboard blocked */ }
  };

  return (
    <div
      className="min-h-[calc(100vh-4rem)] relative overflow-hidden"
      style={{
        background: "radial-gradient(1000px 500px at 15% -10%, rgba(224,94,54,0.16), transparent 60%), radial-gradient(1100px 600px at 90% 8%, rgba(45,90,39,0.24), transparent 60%), linear-gradient(180deg, #0f1420 0%, #171d2e 100%)",
      }}
      data-testid="leads-new-page"
    >
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
        {/* Header */}
        <div className="text-center text-white mb-6">
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.25em] font-black text-white/60 bg-white/5 border border-white/10 rounded-full px-3 py-1">
            <Sparkles size={12} /> Lead · Ask once, reach many
          </div>
          <h1 className="heading text-4xl md:text-5xl font-black mt-3 leading-tight">
            What do <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#E05E36] to-[#DDA77B]">you need</span> today?
          </h1>
          <p className="text-white/60 mt-2 max-w-xl mx-auto text-sm">
            Type it once in plain English. We work out the category and notify matching providers on Find2Near once you press Broadcast.
          </p>
        </div>

        {/* Prompt form */}
        <form
          onSubmit={submit}
          className="rounded-3xl bg-white/10 backdrop-blur-md border border-white/15 p-3 shadow-2xl"
          data-testid="lead-form"
        >
          <textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            rows={3}
            placeholder="e.g. I need a plumber near me — urgent tap leak, Bandra Mumbai"
            className="w-full bg-transparent outline-none text-white placeholder:text-white/50 text-base resize-none px-4 py-3"
            data-testid="lead-query-input"
          />
          {!user && (
            <div className="grid grid-cols-2 gap-2 px-2 pb-2">
              <Input
                placeholder="Your name (optional)"
                value={contact.name}
                onChange={(e) => setContact(c => ({ ...c, name: e.target.value }))}
                className="rounded-xl bg-black/30 border-white/10 text-white placeholder:text-white/40"
                data-testid="lead-guest-name"
              />
              <Input
                placeholder="Your mobile (required)"
                value={contact.mobile}
                onChange={(e) => setContact(c => ({ ...c, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                className="rounded-xl bg-black/30 border-white/10 text-white placeholder:text-white/40 font-mono"
                data-testid="lead-guest-mobile"
              />
            </div>
          )}
          {user && (
            <label className="flex items-start gap-2 px-3 pb-2 text-[11px] text-white/70" data-testid="lead-share-contact-label">
              <input type="checkbox" checked={shareContact} onChange={(e) => setShareContact(e.target.checked)}
                     data-testid="lead-share-contact" className="mt-0.5" />
              <span>Share my mobile number with matched providers. If unchecked, they can only reply in your Find2Near inbox.</span>
            </label>
          )}
          <div className="flex items-center gap-2 px-2 pb-2">
            {loc?.city && (
              <div className="inline-flex items-center gap-1 text-[11px] text-white/60" data-testid="lead-location-city">
                <MapPin size={11} /> {loc.city}
              </div>
            )}
            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="ml-auto inline-flex items-center gap-1.5 rounded-2xl px-5 py-2.5 text-sm font-black text-white bg-gradient-to-r from-[#E05E36] to-[#c94e2a] hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              data-testid="lead-submit"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {loading ? "Sending…" : "Broadcast to providers"}
            </button>
          </div>
        </form>

        {/* Sample chips */}
        {!result && !loading && (
          <div className="mt-4 flex flex-wrap justify-center gap-2" data-testid="lead-samples">
            {SAMPLE_QUERIES.map(s => (
              <button
                key={s}
                type="button"
                onClick={() => setQuery(s)}
                className="text-[11px] text-white/70 border border-white/15 rounded-full px-3 py-1 hover:bg-white/10 hover:border-white/30 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="mt-6 space-y-4" data-testid="lead-result">
            {/* Parsed intent */}
            <div className="rounded-2xl bg-white/95 border border-white/20 shadow-2xl p-5" data-testid="lead-parsed">
              <div className="text-[10px] uppercase tracking-widest font-black text-stone-500 mb-2">We understood</div>
              <div className="flex flex-wrap gap-2 items-center">
                <Chip color="bg-[#E05E36] text-white">{result.parsed.category}</Chip>
                {result.parsed.city && <Chip color="bg-emerald-100 text-emerald-800 border border-emerald-200">📍 {result.parsed.city}</Chip>}
                <Chip color={URGENCY_LABEL[result.parsed.urgency]?.color || "bg-stone-100 text-stone-700 border-stone-200"}>
                  {URGENCY_LABEL[result.parsed.urgency]?.label || result.parsed.urgency}
                </Chip>
                {(result.parsed.keywords || []).slice(0, 6).map(k => (
                  <Chip key={k} color="bg-stone-100 text-stone-700 border border-stone-200">{k}</Chip>
                ))}
              </div>
            </div>

            {/* Broadcast summary */}
            <div className={`rounded-2xl border p-5 ${result.broadcast_count > 0 ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="lead-broadcast-summary">
              <div className="flex items-center gap-2 mb-2">
                {result.broadcast_count > 0 ? (
                  <><CheckCircle2 className="text-emerald-600" size={18} />
                  <div className="font-black text-emerald-900">
                    Sent to {result.broadcast_count} registered provider{result.broadcast_count === 1 ? "" : "s"}
                  </div></>
                ) : (
                  <><AlertTriangle className="text-amber-600" size={18} />
                  <div className="font-black text-amber-900">
                    No registered providers matched — share the invite below
                  </div></>
                )}
              </div>
              {result.notified_providers?.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {result.notified_providers.slice(0, 6).map(p => (
                    <div key={p.provider_user_id} className="flex items-center gap-2 text-sm text-stone-700" data-testid={`lead-provider-${p.provider_user_id}`}>
                      <div className="w-7 h-7 rounded-full bg-stone-200 overflow-hidden grid place-items-center shrink-0">
                        {p.image ? <img loading="lazy" src={p.image} alt="" className="w-full h-full object-cover" /> : <UserIcon size={12} className="text-stone-400" />}
                      </div>
                      <div className="truncate flex-1">
                        <b>{p.provider_name}</b>
                        <span className="text-stone-500"> · {p.city || "—"}</span>
                      </div>
                      <Link to={`/messages#${p.conv_id}`} className="text-[11px] font-bold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1">
                        Chat <MessageCircle size={11} />
                      </Link>
                    </div>
                  ))}
                  {result.notified_providers.length > 6 && (
                    <div className="text-xs text-stone-500 pl-9">+ {result.notified_providers.length - 6} more…</div>
                  )}
                </div>
              )}
            </div>

            {/* Share invite — safe fallback for reaching non-registered providers */}
            <div className="rounded-2xl bg-white/95 border border-white/20 shadow-2xl p-5" data-testid="lead-invite">
              <div className="flex items-center gap-2 mb-2">
                <Share2 size={16} className="text-[#E05E36]" />
                <div className="font-black text-stone-900">Know someone who could help?</div>
              </div>
              <div className="text-sm text-stone-600 mb-3">
                Send this to any local worker (plumber, electrician…) via your own WhatsApp.
                They'll get your request and can reply in seconds — no signup needed to view.
              </div>
              <div className="rounded-xl bg-stone-100 border border-stone-200 p-3 text-xs font-mono text-stone-700 whitespace-pre-wrap" data-testid="lead-invite-text">
                {result.invite.whatsapp_share_text}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <a
                  href={result.invite.whatsapp_deeplink}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] hover:bg-[#1eb958] text-white text-xs font-black px-4 py-2 transition-colors"
                  data-testid="lead-share-whatsapp"
                >
                  <Phone size={12} /> Share on WhatsApp
                </a>
                <button
                  type="button"
                  onClick={() => copy(result.invite.whatsapp_share_text)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs font-bold px-4 py-2 transition-colors"
                  data-testid="lead-copy-invite"
                >
                  <Copy size={12} /> Copy
                </button>
                <Link
                  to="/inbox"
                  className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-stone-200 hover:border-stone-400 text-stone-700 text-xs font-bold px-4 py-2 transition-colors"
                  data-testid="lead-open-inbox"
                >
                  <Zap size={12} /> Watch replies in Inbox
                </Link>
              </div>
            </div>

            {result.possible_duplicate_of && (
              <div className="rounded-xl bg-amber-500/15 text-amber-200 text-xs p-3" data-testid="lead-duplicate-note">
                You posted a similar request in the last 24h. <Link to={`/leads/${result.possible_duplicate_of}/deal`} className="underline font-bold">Open that one</Link> instead?
              </div>
            )}
            <Link to={`/leads/${result.lead_id}/deal`} data-testid="lead-open-deal"
                  className="block text-center rounded-full bg-white text-stone-900 font-black py-2.5 text-sm">View quotes &amp; book</Link>

            {/* New request button */}
            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => { setResult(null); setQuery(""); }}
                className="text-white/60 hover:text-white text-xs font-bold underline decoration-dotted"
                data-testid="lead-new-request"
              >
                Post another request
              </button>
            </div>
          </div>
        )}

        {/* Footer legal note */}
        <div className="mt-10 text-center text-[11px] text-white/40 max-w-md mx-auto leading-relaxed">
          Leads are broadcast only to <b className="text-white/70">registered</b> Find2Near providers who opted in. We do not scrape Google, IndiaMart, JustDial or WhatsApp — the share button uses your own WhatsApp so you stay in control.
        </div>
      </div>
    </div>
  );
}

function Chip({ children, color }) {
  return <span className={`text-[11px] font-bold rounded-full px-2.5 py-1 ${color}`}>{children}</span>;
}
